import { BehaviorSubject } from 'rxjs';
import { addError } from './Error';
import {
  getCustomerRunTotals,
  getRPMAcctRunTotals,
  getTicketRunTotals,
  getCustomerSupplierRunTotals
} from '../Api/RunTotal/RunTotalApi';

export interface RpmRun {
  id: number;
  runIsClosed: boolean;
  runYear: number;
  runMonthAbbreviation: string;
  runQuarter: number;
  runMonthNumber: number;
  runYearMonth: string;
  runLastDayOfMonth: string;
  runFirstDayOfMonth: string;
  runDateClosed: string;
  runDateOpened: string;
  runKey: number;
  runMonthName: string;
}

export interface BaseRunTotal {
  id: string;
  rpm_run_id: string;
  charge_onetime_expected: number | null;
  charge_onetime_actual: number | null;
  charge_recurring_expected: number | null;
  charge_recurring_actual: number | null;
  revenue_onetime_expected: number | null;
  revenue_onetime_actual: number | null;
  revenue_recurring_expected: number | null;
  revenue_recurring_actual: number | null;
  revenue_trueup_expected: number | null;
  revenue_trueup_actual: number | null;
  revenue_spiff_expected: number | null;
  revenue_spiff_actual: number | null;
  charge_onetime_difference: number | null;
  charge_recurring_difference: number | null;
  revenue_onetime_difference: number | null;
  revenue_recurring_difference: number | null;
  revenue_spiff_difference: number | null;
  revenue_trueup_difference: number | null;
  revenue_recurring_expected_percent: number | null;
  revenue_recurring_actual_percent: number | null;
  revenue_recurring_difference_percent: number | null;
  commission_item_count: number | null;
  run: RpmRun;
  createdAt?: string;
  updatedAt?: string;
}

export interface CustomerRunTotal extends BaseRunTotal {
  customer_id: string;
  supplier_id?: string;
}

export interface TicketRunTotal extends BaseRunTotal {
  ticket_id: string;
  supplier_id: string;
}

export interface RPMAcctRunTotal extends BaseRunTotal {
  rpm_account_id: string;
}

export interface RunTotalResponse<T extends BaseRunTotal> {
  sum: {
    charge_onetime_expected: number;
    charge_onetime_actual: number;
    charge_recurring_expected: number;
    charge_recurring_actual: number;
    revenue_onetime_expected: number;
    revenue_onetime_actual: number;
    revenue_recurring_expected: number;
    revenue_recurring_actual: number;
    revenue_trueup_expected: number;
    revenue_trueup_actual: number;
    revenue_spiff_expected: number;
    revenue_spiff_actual: number;
    charge_onetime_difference: number;
    charge_recurring_difference: number;
    revenue_onetime_difference: number;
    revenue_recurring_difference: number;
    revenue_spiff_difference: number;
    revenue_trueup_difference: number;
    revenue_recurring_expected_percent: number;
    revenue_recurring_actual_percent: number;
    revenue_recurring_difference_percent: number;
  };
  totals: T[];
}

const runTotalsByCustomerSubject = new BehaviorSubject<RunTotalResponse<CustomerRunTotal> | null>(null);
const runTotalsByTicketSubject = new BehaviorSubject<RunTotalResponse<TicketRunTotal> | null>(null);
const runTotalsByRpmAcctSubject = new BehaviorSubject<RunTotalResponse<RPMAcctRunTotal> | null>(null);

const runTotalsByCustomerLoadingSubject = new BehaviorSubject<boolean>(false);
const runTotalsByTicketLoadingSubject = new BehaviorSubject<boolean>(false);
const runTotalsByRpmAcctLoadingSubject = new BehaviorSubject<boolean>(false);

const clearError = () => addError('run-total', undefined);

export const getRunTotalsByCustomerSubject = (): BehaviorSubject<RunTotalResponse<CustomerRunTotal> | null> =>
  runTotalsByCustomerSubject;
export const getRunTotalsByTicketSubject = (): BehaviorSubject<RunTotalResponse<TicketRunTotal> | null> =>
  runTotalsByTicketSubject;
export const getRunTotalsByRpmAcctSubject = (): BehaviorSubject<RunTotalResponse<RPMAcctRunTotal> | null> =>
  runTotalsByRpmAcctSubject;

export const getRunTotalsByCustomerLoadingSubject = (): BehaviorSubject<boolean> => runTotalsByCustomerLoadingSubject;
export const getRunTotalsByTicketLoadingSubject = (): BehaviorSubject<boolean> => runTotalsByTicketLoadingSubject;
export const getRunTotalsByRpmAcctLoadingSubject = (): BehaviorSubject<boolean> => runTotalsByRpmAcctLoadingSubject;

export const fetchRunTotalsByCustomerId = async (id: string, supplierId?: string) => {
  runTotalsByCustomerLoadingSubject.next(true);

  const { data, error } = await (supplierId ? getCustomerSupplierRunTotals(id, supplierId) : getCustomerRunTotals(id));

  if (data) {
    clearError();
    runTotalsByCustomerSubject.next(data);
  }
  if (error) addError('run-total', error.message);

  runTotalsByCustomerLoadingSubject.next(false);
};

export const fetchRunTotalsByTicketId = async (id: string) => {
  runTotalsByTicketLoadingSubject.next(true);

  const { data, error } = await getTicketRunTotals(id);

  if (data) {
    clearError();
    runTotalsByTicketSubject.next(data);
  }
  if (error) addError('run-total', error.message);

  runTotalsByTicketLoadingSubject.next(false);
};

export const fetchRunTotalsByRpmAcctId = async (id: string) => {
  runTotalsByRpmAcctLoadingSubject.next(true);

  const { data, error } = await getRPMAcctRunTotals(id);

  if (data) {
    clearError();
    runTotalsByRpmAcctSubject.next(data);
  }
  if (error) addError('run-total', error.message);

  runTotalsByRpmAcctLoadingSubject.next(false);
};
